import { HttpStatusCode } from 'axios';
import { useEffect, useState } from 'react';
import { ClipLoader } from 'react-spinners';
import {
  ReferralMetrics,
  ReferralPayoutObject,
} from '../../../../../types/apiTypes';
import useApi from '../../../../../utils/AxiosWrapper';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import InviteReferralModal from './widgets/InviteReferralModal';
import ReferralMetricsWidget from './widgets/ReferralMetricsWidget';
import ReferralPayoutSummary from './widgets/ReferralPayoutSummary';
import ReferralTopBar from './widgets/ReferralTopBar';

interface VendorReferralsOverviewProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function VendorReferralsOverview({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: VendorReferralsOverviewProps) {
  const { screenView } = useScreenView();
  const { callProtectedApi } = useApi();
  const { userId } = useUser();
  const [referralPopupVisible, setReferralPopupVisible] = useState(false);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [loadingPayoutSummary, setLoadingPayoutSummary] = useState(false);
  const [metrics, setMetrics] = useState<ReferralMetrics | null>(null);
  const [payoutSummary, setPayoutSummary] = useState<
    ReferralPayoutObject[] | null
  >(null);
  useEffect(() => {
    const fetchReferralMetrics = async (vendor_id: string) => {
      setLoadingMetrics(true);
      try {
        const response = await callProtectedApi(
          `/referrals/metrics/vendor/${vendor_id}`,
          'get',
          {}
        );
        const data = response?.data?.data?.metrics;
        if (
          data &&
          Object.entries(data)?.length > 0 &&
          response?.status === HttpStatusCode.Ok
        ) {
          setMetrics(data);
        }
      } catch (e: any) {
        console.error('Error occurred while fetching referral metrics: ', e);
      } finally {
        setLoadingMetrics(false);
      }
    };

    const fetchReferralPayoutSummary = async (vendor_id: string) => {
      setLoadingPayoutSummary(true);
      try {
        const response = await callProtectedApi(
          `/referrals/payout-summary/vendor/${vendor_id}`,
          'get',
          {}
        );
        const data = response?.data?.data?.summary;
        if (
          data &&
          data?.length > 0 &&
          response?.status === HttpStatusCode.Ok
        ) {
          setPayoutSummary(data);
        }
      } catch (e: any) {
        console.error('Error occurred while fetching referral metrics: ', e);
      } finally {
        setLoadingPayoutSummary(false);
      }
    };

    fetchReferralMetrics(userId ?? '');
    fetchReferralPayoutSummary(userId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateReferralPopupVisible = (visible: boolean) => {
    setReferralPopupVisible(visible);
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="overflow-y-auto overflow-x-hidden px-4 sm:w-[95%] md:w-full lg:w-[1148px] lg:max-w-[1148px] lg:px-0">
            {referralPopupVisible && (
              <InviteReferralModal
                onClose={() => setReferralPopupVisible(false)}
                isOpen={referralPopupVisible}
              />
            )}
            {/* <OnboardingModal /> */}
            <div className="flex h-full w-full flex-col space-y-[16px] py-[16px] pb-12">
              <ReferralTopBar
                setReferralPopupVisible={updateReferralPopupVisible}
              />
              <div className="flex w-full flex-row justify-center">
                {!loadingMetrics ? (
                  <ReferralMetricsWidget referralMetrics={metrics} />
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={loadingMetrics}
                    size={40}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="flex h-full w-full justify-center"
                  />
                )}
              </div>
              <div className="flex w-full flex-row space-x-4">
                <ReferralPayoutSummary
                  payoutObjects={payoutSummary ?? []}
                  loadingPayoutSummary={loadingPayoutSummary}
                />{' '}
              </div>
            </div>
          </div>
        );
    }
  };
  return <>{renderScreenView(screenView)}</>;
}

export default VendorReferralsOverview;
